import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useHttp from '../../../hooks/use-http';
import i18n from '../../../i18n';
import LoadingCard from '../ProductCard/LoadingCard';
import ProductCard from '../ProductCard/ProductCard';
import { PartSlot } from '../../../partSlot';
import {
    deriveAttributeFiltersFromProducts,
    getMatchingProductObjectsByAttrMap,
} from './variantUtils';

const PRODUCT_API = '/rest/product/list_store/';

const getCurrentSearch = (fallback = '') => {
    if (typeof window === 'undefined') return fallback || '';
    return window.location.search || fallback || '';
};

const normalizeBool = (v, defaultValue = true) => {
    if (v === undefined || v === null || v === '') return defaultValue;
    const s = String(v).toLowerCase().trim();
    if (['true', '1', 'yes', 'y', 'on'].includes(s)) return true;
    if (['false', '0', 'no', 'n', 'off'].includes(s)) return false;
    return defaultValue;
};

const parseFields = (value, fallback = []) => {
    if (!value) return fallback;
    if (Array.isArray(value)) return value;
    const raw = String(value).trim();
    if (!raw) return fallback;
    try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed;
    } catch (e) {
        // ignore
    }
    return raw.split(',').map((s) => s.trim()).filter(Boolean);
};

const ensureHistoryLocationChangeEvents = () => {
    if (typeof window === 'undefined') return () => {};
    if (window.__smr_history_locationchange_installed) return () => {};

    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    const dispatch = () => {
        window.dispatchEvent(new Event('locationchange'));
    };

    window.history.pushState = function pushStatePatched(...args) {
        const ret = originalPushState.apply(this, args);
        dispatch();
        return ret;
    };
    window.history.replaceState = function replaceStatePatched(...args) {
        const ret = originalReplaceState.apply(this, args);
        dispatch();
        return ret;
    };

    window.addEventListener('popstate', dispatch);
    window.__smr_history_locationchange_installed = true;

    return () => {
        try {
            window.removeEventListener('popstate', dispatch);
        } catch (e) {
            // ignore
        }
    };
};

export default function ProductStoreList({
    initialData = null,
    flexWrap = true,
    fields = ['name', 'price', 'price_discount', 'variants', 'add_to_cart'],
    cardComponent = '',
}) {
    const initialSearch = getCurrentSearch(initialData?.search || '');
    const initialProductsData = initialData?.products || null;

    const [products, setProducts] = useState(() => initialProductsData?.results || []);
    const [nextUrl, setNextUrl] = useState(() => initialProductsData?.next || null);
    const [productCount, setProductCount] = useState(() => initialProductsData?.count || initialProductsData?.results?.length || 0);
    const [hasMore, setHasMore] = useState(() => initialProductsData ? !!initialProductsData.next : true);
    const loadMoreTriggerRef = useRef(null);

    const [urlSearch, setUrlSearch] = useState(() => initialSearch);
    const activeProductQueryRef = useRef(0);
    const productRequestControllersRef = useRef(new Set());
    const bootstrappedSearchRef = useRef(initialProductsData ? initialSearch : null);
    const nextPageGuardRef = useRef({ inFlight: false, lastUrl: null });

    const { filters: attributeFilters, attrMap: attributeAttrMap } = useMemo(() => {
        const result = deriveAttributeFiltersFromProducts(products);
        return result.filters ? result : { filters: {}, attrMap: {} };
    }, [products]);

    const processProducts = useCallback((data, { queryId, append } = {}) => {
        if (queryId !== activeProductQueryRef.current) return;
        if (data && data.results) {
            setProducts(prev => append ? prev.concat(data.results) : data.results);
            setNextUrl(data.next || null);
            setProductCount(data.count || data.results.length);
            setHasMore(!!data.next);
        }
    }, []);

    const { isLoading: productsLoading, error: productsError, sendRequest: fetchProducts } = useHttp(processProducts);

    const cancelInFlightProductRequests = useCallback(() => {
        productRequestControllersRef.current.forEach((controller) => controller.abort());
        productRequestControllersRef.current.clear();
    }, []);

    const requestProductsPage = useCallback((url, { replace = false, onFinally } = {}) => {
        if (replace) {
            activeProductQueryRef.current += 1;
            cancelInFlightProductRequests();
            nextPageGuardRef.current = { inFlight: false, lastUrl: null };
        }

        const queryId = activeProductQueryRef.current;
        const controller = new AbortController();
        productRequestControllersRef.current.add(controller);

        fetchProducts(
            { url, json: true, no_auth: true, signal: controller.signal },
            { queryId, append: !replace }
        ).finally(() => {
            productRequestControllersRef.current.delete(controller);
            if (typeof onFinally === 'function') onFinally();
        });
    }, [cancelInFlightProductRequests, fetchProducts]);

    useEffect(() => {
        const cleanup = ensureHistoryLocationChangeEvents();
        const onLocationChange = () => setUrlSearch(window.location.search || '');
        window.addEventListener('locationchange', onLocationChange);
        window.addEventListener('popstate', onLocationChange);
        return () => {
            window.removeEventListener('locationchange', onLocationChange);
            window.removeEventListener('popstate', onLocationChange);
            cleanup();
        };
    }, []);

    useEffect(() => {
        return () => cancelInFlightProductRequests();
    }, [cancelInFlightProductRequests]);

    // URL -> products fetch (URL is the driver of the API query).
    useEffect(() => {
        if (bootstrappedSearchRef.current === (urlSearch || '')) {
            bootstrappedSearchRef.current = null;
            return;
        }

        setProducts([]);
        setNextUrl(null);
        setProductCount(0);
        setHasMore(true);
        const url = urlSearch ? `${PRODUCT_API}${urlSearch}` : PRODUCT_API;
        requestProductsPage(url, { replace: true });
    }, [urlSearch, requestProductsPage]);

    const loadMore = useCallback(() => {
        if (!nextUrl) return;
        if (productsLoading) return;

        const guard = nextPageGuardRef.current;
        // IntersectionObserver can fire multiple times while the sentinel stays visible.
        // Prevent duplicate requests for the same `nextUrl` and avoid concurrent pagination calls.
        if (guard.inFlight) return;
        if (guard.lastUrl && String(guard.lastUrl) === String(nextUrl)) return;

        nextPageGuardRef.current = { inFlight: true, lastUrl: nextUrl };
        requestProductsPage(nextUrl, {
            onFinally: () => {
                // Clear "inFlight" only; keep lastUrl so we don't refetch same page if still visible.
                nextPageGuardRef.current = {
                    inFlight: false,
                    lastUrl: nextPageGuardRef.current.lastUrl,
                };
            },
        });
    }, [nextUrl, productsLoading, requestProductsPage]);

    useEffect(() => {
        const triggerElement = loadMoreTriggerRef.current;
        if (!triggerElement || !hasMore || !nextUrl) return undefined;

        const observer = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                if (entry && entry.isIntersecting && !productsLoading) {
                    loadMore();
                }
            },
            { root: null, rootMargin: '200px', threshold: 0.1 }
        );

        observer.observe(triggerElement);
        return () => observer.disconnect();
    }, [hasMore, nextUrl, productsLoading, loadMore]);

    const filteredProducts = useMemo(() => {
        if (Object.keys(attributeFilters).length === 0) return products;

        const params = new URLSearchParams((urlSearch || '').startsWith('?') ? (urlSearch || '').slice(1) : (urlSearch || ''));
        const attrFilterValues = {};
        Object.keys(attributeFilters).forEach((sc) => {
            const many = params.getAll(`${sc}__in`).filter(Boolean);
            if (many.length > 0) {
                attrFilterValues[sc] = many;
                return;
            }
            const single = params.get(sc);
            if (single != null && single !== '') attrFilterValues[sc] = single;
        });

        if (Object.keys(attrFilterValues).length === 0) return products;
        return products.filter((p) => {
            const matching = getMatchingProductObjectsByAttrMap(p, attrFilterValues, attributeAttrMap);
            return matching.length > 0;
        });
    }, [products, urlSearch, attributeFilters, attributeAttrMap]);

    const loadProducts = [...Array(12).keys()].map(id => <LoadingCard key={id} />);

    const wrap = normalizeBool(flexWrap, true);
    const wantedFields = parseFields(fields, ['name', 'price', 'price_discount', 'variants', 'add_to_cart']);

    return (
        <div className="store-wrapper">
            <h5 className="mb-3">
                {Object.keys(attributeFilters).length > 0 && filteredProducts.length !== products.length
                    ? `${filteredProducts.length} ${i18n.t('Products')}`
                    : `${productCount} ${i18n.t('Products')}`}
            </h5>

            <div
                className="store-row"
                style={wrap ? undefined : { flexWrap: 'nowrap', overflowX: 'auto' }}
            >
                {(productsLoading && products.length === 0) ? (
                    loadProducts
                ) : productsError ? (
                    <h6 className="text-danger">{i18n.t('Something went wrong loading products')}</h6>
                ) : filteredProducts.length > 0 ? (
                    filteredProducts.map((product) => (
                        <PartSlot
                            key={`${product?.id ?? product?.product_object?.id ?? product?.url ?? product?.product_object?.url}-${product?.store_group ?? ''}`}
                            id={cardComponent || 'ProductCard'}
                            props={{ product, fields: wantedFields }}
                        >
                            <ProductCard product={product} fields={wantedFields} />
                        </PartSlot>
                    ))
                ) : (
                    <h5 className="ms-3">{i18n.t('No products found')}</h5>
                )}
            </div>

            {hasMore && nextUrl && (
                <div
                    ref={loadMoreTriggerRef}
                    className="mt-4 pt-3 border-top border-secondary border-opacity-25"
                >
                    {productsLoading && products.length > 0 ? (
                        <div
                            className="d-flex flex-column align-items-center justify-content-center gap-2 py-3"
                            role="status"
                            aria-live="polite"
                            aria-busy="true"
                        >
                            <div
                                className="spinner-border text-primary"
                                style={{ width: '2.25rem', height: '2.25rem' }}
                                aria-hidden="true"
                            />
                            <span className="small text-muted mb-0">
                                {i18n.t('Loading more products')}
                            </span>
                        </div>
                    ) : (
                        <div className="py-2" aria-hidden="true" />
                    )}
                </div>
            )}
        </div>
    );
}

